import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { apiUrl } from '../utils/constants.ts'; // Ruta corregida (asumiendo que constants está en src/utils)
import ClipLoader from 'react-spinners/ClipLoader';

// Import default profile pictures - Ruta corregida y extensión
import profil1 from '../assets/profil1.jpg'; 
import profil2 from '../assets/profil2.png';
import profil3 from '../assets/profil3.png';

interface ProfilePictureModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentPicture: string; // 
    walletAddress: string | undefined;
    onPictureUpdate: (newPicture: string) => void; // Callback to update parent state
}

const pictureOptions = [
    { id: 'profil1.jpg', src: profil1 }, 
    { id: 'profil2.png', src: profil2 },
    { id: 'profil3.png', src: profil3 },
];

const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
    isOpen,
    onClose,
    currentPicture,
    walletAddress,
    onPictureUpdate,
}) => {
    const [selectedPicture, setSelectedPicture] = useState(currentPicture);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { address } = useAccount(); // Get connected address for verification

    useEffect(() => {
        // Reset state when modal opens or closes
        if (isOpen) {
            setSelectedPicture(currentPicture || 'profil1.jpg'); // Default to profil1.jpg
            setError(null);
            setIsLoading(false);
        } else {
            // Don't reset selectedPicture on close, keep the last state until reopened
            setError(null);
            setIsLoading(false);
        }
    }, [isOpen, currentPicture]);

    const handleSave = async () => {
        if (!walletAddress || !address || walletAddress.toLowerCase() !== address.toLowerCase()) {
            setError("Wallet address mismatch or not connected.");
            return;
        }
        if (!selectedPicture) {
            setError("Please select a profile picture.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiUrl}/api/profile/picture`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    walletAddress: address, // Send connected address for verification
                    pictureFilename: selectedPicture,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                onPictureUpdate(selectedPicture); // Update parent state
                onClose(); // Close modal on success
            } else {
                setError(result.error || "An unknown error occurred.");
            }
        } catch (err: any) {
            console.error("Failed to update profile picture:", err);
            setError(err.message || "Failed to update profile picture. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-[#2c3e50] to-[#34495e] p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-500 relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-300 hover:text-white text-2xl font-bold"
                    disabled={isLoading}
                >
                    &times;
                </button>
                <h2 className="text-2xl font-bold mb-6 text-white text-center">Choose Profile Picture</h2>

                <div className="flex justify-around items-center mb-6">
                    {pictureOptions.map((pic) => (
                        <button
                            key={pic.id}
                            onClick={() => setSelectedPicture(pic.id)}
                            className={`rounded-full border-4 p-1 transition-all duration-150 ${selectedPicture === pic.id ? 'border-[#7db4e5]' : 'border-transparent hover:border-gray-500'}`}
                            disabled={isLoading}
                        >
                            <img
                                src={pic.src}
                                alt={`Profile option ${pic.id.charAt(6)}`} // Esto podría necesitar ajuste si el id cambia mucho
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        </button>
                    ))}
                </div>


                {error && (
                    <p className="text-red-400 text-sm mb-3 text-center">{error}</p>
                )}

                <div className="flex justify-center">
                    <button
                        onClick={handleSave}
                        className="bg-[#7db4e5] hover:bg-[#6ca3d1] text-white font-bold py-2 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                        disabled={isLoading || !selectedPicture}
                    >
                        {isLoading ? (
                            <ClipLoader color={'#ffffff'} loading={true} size={20} />
                        ) : (
                            'Save Picture'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfilePictureModal;
